<script setup lang="ts">
import { computed, watch } from 'vue'

import { Overlay } from '@collector/shared-ui'
import { nullCoalesce } from '@collector/shared-utils'
import {
  useEvent,
  useParticipantResultsMaps,
  useParticipants,
} from '@desktop/views/Relation/composables'
import { injectPitchPenaltyOverlayConfig } from '@desktop/views/Relation/Sports/provide-inject'

import PenaltyTeamResult from './PenaltyTeamResult.vue'
import { usePenaltyChips } from './usePenaltyChips'

interface Emits {
  visible: [value: boolean]
}

const emit = defineEmits<Emits>()

const pitchPenaltyOverlayConfig = injectPitchPenaltyOverlayConfig()

const { event } = useEvent()
const { homeParticipant, awayParticipant } = useParticipants()
const { homeParticipantResultsMap, awayParticipantResultsMap } =
  useParticipantResultsMaps()

const { homeChips, awayChips } = usePenaltyChips(
  homeParticipant,
  awayParticipant,
  pitchPenaltyOverlayConfig,
)

const visible = computed(() => {
  return event.value.status_id === pitchPenaltyOverlayConfig.penaltyStatusId
})

watch(visible, (visible) => emit('visible', visible), {
  immediate: true,
})
</script>

<template>
  <Overlay
    class="bg-success-1/70"
    centered
    :modelValue="visible"
  >
    <div class="@2md:p-3 h-full w-full p-2 text-white">
      <div class="@2md:text-xl text-center text-xs font-bold">
        Penalty shootout
      </div>
      <div
        class="@2md:text-4xl grid grid-cols-[1fr_auto_1fr] gap-x-1 text-lg font-bold"
      >
        <div class="text-right">
          {{
            nullCoalesce(
              homeParticipantResultsMap[
                pitchPenaltyOverlayConfig.penaltyResultId
              ]?.value,
              0,
            )
          }}
        </div>
        <div>:</div>
        <div class="text-left">
          {{
            nullCoalesce(
              awayParticipantResultsMap[
                pitchPenaltyOverlayConfig.penaltyResultId
              ]?.value,
              0,
            )
          }}
        </div>
      </div>
      <div class="@2md:mt-1 grid grid-cols-[50%_50%]">
        <PenaltyTeamResult
          v-if="homeParticipant"
          class="pr-3"
          :participant="homeParticipant"
          :chips="homeChips"
        />
        <PenaltyTeamResult
          v-if="awayParticipant"
          class="pl-3"
          :participant="awayParticipant"
          :chips="awayChips"
          reversed
        />
      </div>
    </div>
  </Overlay>
</template>
